import { Component, Inject, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Notification, NotificationDetail, NotificationType } from 'src/app/models/notification.model';
import { NotificationService } from 'src/app/services/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { UserLocaleService } from 'src/app/services/user-locale.service';
import { catchError, EMPTY, Subject, takeUntil, tap } from 'rxjs';
import { DateFormatLocalizationService } from 'src/app/core/services/date-format-localization.service';

export interface NotificationModalData {
  notification: Notification;
  autoMarkAsRead?: boolean;
}

export interface NotificationModalResult {
  markedAsRead: boolean;
  markedAsUnread?: boolean;
}

@Component({
  selector: 'app-notification-detail-modal',
  templateUrl: './notification-detail-modal.component.html',
  styleUrls: ['./notification-detail-modal.component.scss'],
})
export class NotificationDetailModalComponent implements OnInit, OnDestroy {
  isMarkingAsRead = false;
  isMarkingAsUnread = false;
  private destroy$ = new Subject<void>();
  private notificationDetail = signal<NotificationDetail | null>(null);

  readonly displayTitle = computed(() => {
    const detail = this.notificationDetail();
    const locale = this.localeService.currentLocale();

    if (detail?.dispatch) {
      return this.notificationService.getLocalizedTitle(detail.dispatch, locale);
    }

    return detail?.title || this.data.notification.title;
  });

  readonly displayMessage = computed(() => {
    const detail = this.notificationDetail();
    const locale = this.localeService.currentLocale();

    if (detail?.dispatch) {
      return this.notificationService.getLocalizedMessage(detail.dispatch, locale);
    }

    return detail?.message || this.data.notification.message;
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: NotificationModalData,
    private dialogRef: MatDialogRef<NotificationDetailModalComponent, NotificationModalResult>,
    private notificationService: NotificationService,
    private localeService: UserLocaleService,
    private translate: TranslateService,
    private dateFormatLocalization: DateFormatLocalizationService
  ) { }

  ngOnInit(): void {
    this.notificationService.getNotificationById(this.data.notification.id)
      .pipe(
        takeUntil(this.destroy$),
        tap((detail) => {
          if (detail) {
            this.notificationDetail.set(detail);
          }
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get notification(): Notification {
    return this.data.notification;
  }

  get icon(): string {
    return this.getNotificationIcon(this.notification.type);
  }

  get typeClass(): string {
    return this.getTypeClass(this.notification.type);
  }

  get showMarkAsReadButton(): boolean {
    return !this.data.autoMarkAsRead && !this.notification.isRead;
  }

  get showMarkAsUnreadButton(): boolean {
    return this.notification.isRead;
  }

  markAsRead(): void {
    if (this.isMarkingAsRead || this.notification.isRead) {
      return;
    }

    this.isMarkingAsRead = true;

    this.notificationService.markAsRead(this.notification.id).pipe(
      takeUntil(this.destroy$),
      tap(() => {
        this.notification.isRead = true;
        this.isMarkingAsRead = false;
        this.dialogRef.close({ markedAsRead: true });
      }),
      catchError(() => {
        this.isMarkingAsRead = false;
        return EMPTY;
      })
    ).subscribe();
  }

  markAsUnread(): void {
    if (this.isMarkingAsUnread || !this.notification.isRead) {
      return;
    }

    this.isMarkingAsUnread = true;

    this.notificationService.markAsUnread(this.notification.id).pipe(
      takeUntil(this.destroy$),
      tap(() => {
        this.notification.isRead = false;
        this.isMarkingAsUnread = false;
        this.dialogRef.close({ markedAsRead: false, markedAsUnread: true });
      }),
      catchError(() => {
        this.isMarkingAsUnread = false;
        return EMPTY;
      })
    ).subscribe();
  }

  close(): void {
    if (this.data.autoMarkAsRead && !this.notification.isRead) {
      this.markAsReadAndClose();
    } else {
      this.dialogRef.close({ markedAsRead: false });
    }
  }

  private markAsReadAndClose(): void {
    this.isMarkingAsRead = true;

    this.notificationService.markAsRead(this.notification.id).pipe(
      takeUntil(this.destroy$),
      tap(() => {
        this.notification.isRead = true;
        this.dialogRef.close({ markedAsRead: true });
      }),
      catchError(() => {
        this.dialogRef.close({ markedAsRead: false });
        return EMPTY;
      })
    ).subscribe();
  }

  getRelativeTime(date: Date | string): string {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffMs = now.getTime() - notificationDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return this.translate.instant('notifications.time.justNow');
    } else if (diffMins < 60) {
      return this.translate.instant('notifications.time.minutesAgo', { minutes: diffMins });
    } else if (diffHours < 24) {
      return this.translate.instant('notifications.time.hoursAgo', { hours: diffHours });
    } else if (diffDays < 7) {
      return this.translate.instant('notifications.time.daysAgo', { days: diffDays });
    } else {
      return this.dateFormatLocalization.formatDate(notificationDate);
    }
  }

  private getNotificationIcon(type: NotificationType): string {
    switch (type) {
      case NotificationType.SUCCESS:
        return '/assets/icons/check-circle.svg';
      case NotificationType.WARNING:
        return '/assets/icons/warning.svg';
      case NotificationType.ALERT:
        return '/assets/icons/exclamation.svg';
      case NotificationType.INFO:
      default:
        return '/assets/icons/information-circle.svg';
    }
  }

  private getTypeClass(type: NotificationType): string {
    switch (type) {
      case NotificationType.SUCCESS:
        return 'notification-type-success';
      case NotificationType.WARNING:
        return 'notification-type-warning';
      case NotificationType.ALERT:
        return 'notification-type-error';
      case NotificationType.INFO:
      default:
        return 'notification-type-info';
    }
  }
}
